import type { ConsoleOrganization } from '@sentinel/shared';
import { Button, EmptyState, Panel, Table } from '@sentinel/ui';
import { QueryState } from '@sentinel/web-core';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConsoleCan } from '@/auth/use-console-can';
import { NewOrganizationModal } from './NewOrganizationModal';
import { organizationColumns } from './organization-columns';
import { useOrganizations } from './use-organizations';

const K = 'console.organizations';

/** The organizations of the platform: state, package, what they use; «Nuovo ente» is the
 *  screen's primary action. */
export function OrganizationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canCreate = useConsoleCan('console.organizations:create');
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const { list, provision } = useOrganizations();
  const matches = (o: ConsoleOrganization) =>
    `${o.name} ${o.slug}`.toLowerCase().includes(q.trim().toLowerCase());
  const open = (o: ConsoleOrganization) =>
    void navigate({ to: '/organizations/$organizationId', params: { organizationId: o.id } });

  return (
    <Panel flush title={t(`${K}.title`)}>
      <div className="toolbar">
        <span className="search">
          <input
            type="search"
            placeholder={t(`${K}.search`)}
            aria-label={t('common.labels.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </span>
        {canCreate ? (
          <span className="rt">
            <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
              {t(`${K}.new`)}
            </Button>
          </span>
        ) : null}
      </div>
      <QueryState query={list}>
        {(data) => {
          const rows = data.items.filter(matches);
          return rows.length === 0 ? (
            <EmptyState title={t(`${K}.empty`)} description={t(`${K}.emptyHint`)} />
          ) : (
            <Table
              caption={t(`${K}.title`)}
              columns={organizationColumns(t)}
              rows={rows}
              rowKey={(o) => o.id}
              onRowClick={open}
            />
          );
        }}
      </QueryState>
      {creating ? (
        <NewOrganizationModal
          busy={provision.isPending}
          onClose={() => setCreating(false)}
          onSubmit={(input) =>
            provision.mutate(input, {
              onSuccess: (created) => {
                setCreating(false);
                open(created);
              },
            })
          }
        />
      ) : null}
    </Panel>
  );
}
